import React, { useState, useEffect, useMemo, useRef } from "react";

import {
	coerceToModelType,
	evaluateExpression,
	parseAndFormatExpression,
	preprocessScope,
	valuesEqual,
	toNum
} from "../../../utils/expressionHelpers";
import FlowableNumberErrorComponent from "../Common/ErrorComponent/FlowableNumberErrorComponent";

export const ExpressionField = props => {
	const { config, onChange, lang, Components, form, extraScope, forceValidations } = props;
	const [ touched, setTouched ] = useState(false);
	const lastSentRef = useRef();
	const scope = useMemo(() => {
		const scope = preprocessScope(config.$scope, form);

		return { ...scope, ...extraScope ?? {} };
	}, [ config.$scope, form, extraScope ]);

	const evaluatedExpression = useMemo(() => evaluateExpression(config.$original?.expression, scope),
		[ config.$original.expression, scope ]);

	const { expressionValue, formattedValue } = parseAndFormatExpression(evaluatedExpression, config, lang);

	const expressionConfig = {
		...config,
		enabled: false,
		value: formattedValue
	};

	useEffect(() => {
		const raw = config?.$scope?.[config?.$path];
		setTouched(true);
		if (raw === undefined) {
			// if model has no value yet, write once so dependents don't see `NaN`
			const seed = coerceToModelType(0, expressionValue);
			if (lastSentRef.current !== seed) {
				lastSentRef.current = seed;
				onChange(seed);
			}

			return;
		}

		const normalized = scope?.[config?.$path] ?? raw;
		if (valuesEqual(normalized, expressionValue)) {
			// Normal case: do nothing (no save).
			// Exception: the model currently stores a string, but both sides are numerically equal.
			// In that case we write the numeric value once to normalize the type
			// and prevent future "1" + "2" string concatenation in dependent expressions.
			const a = toNum(normalized);
			const b = toNum(expressionValue);
			if (typeof raw === "string" && !Number.isNaN(a) && !Number.isNaN(b) && a === b) {
				if (lastSentRef.current !== b) {
					lastSentRef.current = b;
					onChange(b);
				}
			}

			return;
		}
		const next = coerceToModelType(raw, expressionValue);
		if (lastSentRef.current === next) {
			return;
		}
		lastSentRef.current = next;

		onChange(next);
	}, [ expressionValue, config.$path, config.$scope, onChange, scope ]);

	return (
		<>
			<Components.text
				{...props}
				config={expressionConfig}
				ckey={config.field}
				key={config.field}
			/>
			{(forceValidations || touched) && (
				<FlowableNumberErrorComponent
					extraSettings={config.extraSettings}
					customValidations={config.customValidations}
				/>
			)}
		</>
	);
};
